import { Container, VBox, VBoxProps } from "./Box";
import React, { ReactElement } from "react";
import { IconName } from "./iconMapping";
import { isIconName } from "./iconHelpers";
import { Icon } from "./Icon";
import { isTMessage, TMessage } from "@/i18n/tt";

import { useTranslation } from "@/stores/RootStoreContext";
import { Button, ButtonProps, LinkButton, LinkButtonProps } from "./Button";
import { DropdownMenu, DropdownMenuProps } from "./Dropdown";
import { Markdown } from "@/text/Markdown";
import { observer } from "mobx-react";
import { MaxWidth } from "./tailwindMappings";

export type EmptyStateProps = {
  illustration: ReactElement | IconName;
  title?: string | TMessage;
  description?: string | ReactElement | TMessage;
  actions?:
    | ReactElement
    | (
        | ReactElement
        | { type: "button"; key: string; props: ButtonProps }
        | { type: "dropdown"; key: string; props: DropdownMenuProps }
        | { type: "link-button"; key: string; props: LinkButtonProps }
      )[];
  child?: React.JSX.Element;
  subtle?: boolean;
  maxWidth?: MaxWidth;
  containerProps?: VBoxProps;
};

export const EmptyState = observer(function EmptyState({
  illustration,
  title,
  description,
  actions,
  child,
  subtle,
  maxWidth,
  containerProps,
}: EmptyStateProps) {
  const { t } = useTranslation();
  return (
    <Container maxWidth={maxWidth ?? "sm"} {...containerProps}>
      <VBox textAlign={"center"} justifyContent={"center"} alignItems={"center"} gap={"lg"}>
        {isIconName(illustration) ? (
          <Icon
            className={subtle ? "opacity-25" : undefined}
            name={illustration}
            size={subtle ? "xxl" : "mega"}
            duotone={!subtle}
          />
        ) : (
          illustration
        )}
        {title && (
          <h3 className={subtle ? "text-base" : undefined}>
            {isTMessage(title) ? t(title) : title}
          </h3>
        )}
        {description && (
          <div>
            {isTMessage(description) ? (
              <Markdown value={t(description)} enableLinks />
            ) : (
              description
            )}
          </div>
        )}
        {actions && (
          <VBox gap={"xl"}>
            {Array.isArray(actions)
              ? actions.map((x) => {
                  switch (true) {
                    case "type" in x && x.type === "button":
                      return <Button key={x.key} {...x.props} />;
                    case "type" in x && x.type === "dropdown":
                      return <DropdownMenu key={x.key} {...x.props} />;
                    case "type" in x && x.type === "link-button":
                      return <LinkButton key={x.key} {...x.props} />;
                    default:
                      return x;
                  }
                })
              : actions}
          </VBox>
        )}
        {child}
      </VBox>
    </Container>
  );
});
